<template>
  <div class="line-eff-page nx-page nx-report-page">
    <nx-report-header
      title="卸车线作业效率分析"
      subtitle="监测单条卸车线效能、班次差异与设备空转，支撑班组绩效与流程优化（数据口径同卸车效率日报）"
    />

    <el-card shadow="never" class="filter-card">
      <el-form inline :model="query" size="small">
        <el-form-item label="作业日期">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            value-format="yyyy-MM-dd"
            unlink-panels
            style="width: 260px"
          />
        </el-form-item>
        <el-form-item label="卸车线">
          <el-select
            v-model="query.lineCodes"
            multiple
            collapse-tags
            clearable
            filterable
            placeholder="全部卸车线"
            style="min-width: 220px"
          >
            <el-option
              v-for="(o, idx) in safeLineOptions"
              :key="'line-' + o.line_code + '-' + idx"
              :label="o.line_label || o.line_code"
              :value="o.line_code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="班次">
          <el-select v-model="query.shiftCodes" multiple collapse-tags clearable placeholder="全部班次" style="width: 200px">
            <el-option label="早班" value="早班" />
            <el-option label="中班" value="中班" />
            <el-option label="晚班" value="晚班" />
          </el-select>
        </el-form-item>
        <el-form-item label="班组">
          <el-select v-model="query.teamNo" clearable filterable placeholder="全部班组" style="min-width: 140px">
            <el-option v-for="t in safeTeamOptions" :key="t.teamNo" :label="t.teamName || t.teamNo" :value="t.teamNo" />
          </el-select>
        </el-form-item>
        <el-form-item label="效率阈值(分)">
          <el-slider v-model="query.efficiencyThreshold" :min="0" :max="100" :show-tooltip="true" style="width: 160px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="applyFilter">查询</el-button>
          <el-button icon="el-icon-download" @click="exportCsv">导出明细</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-row :gutter="16" class="kpi-row">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ fmtNum(kpi.total_tonnage, 0) }} <span class="unit">吨</span></div>
          <div class="kpi-label">总卸车吨位</div>
          <div class="kpi-sub" :class="deltaClass(kpi.tonnageDeltaPercent)">
            较昨日 {{ fmtDeltaPct(kpi.tonnageDeltaPercent) }}
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val">{{ fmtNum(kpi.avg_efficiency_score, 1) }} <span class="unit">分</span></div>
          <div class="kpi-label">平均效率得分</div>
          <div ref="sparkRef" class="spark" />
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val" :class="{ warn: num(kpi.avg_idling_rate) > 10 }">
            {{ fmtNum(kpi.avg_idling_rate, 1) }} <span class="unit">%</span>
          </div>
          <div class="kpi-label">平均设备空转率</div>
          <div class="kpi-sub muted">目标 &lt; 10%</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-val sm">{{ kpi.bestShiftName || '—' }}</div>
          <div class="kpi-label">效率最高班次</div>
          <div class="kpi-sub">{{ fmtNum(kpi.bestShiftScore, 1) }} 分（均分）</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="10">
        <el-card shadow="hover" class="block-card">
          <div class="card-head"><span class="card-title">班次效率对比</span></div>
          <div ref="shiftBarRef" class="echart-box" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="14">
        <el-card shadow="hover" class="block-card">
          <div class="card-head"><span class="card-title">设备空转率趋势（按日）</span></div>
          <div ref="idlingRef" class="echart-box" />
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="block-card">
      <div class="card-head"><span class="card-title">吨位与时间关系</span></div>
      <div ref="bubbleRef" class="echart-box bubble-box" />
    </el-card>

    <el-card shadow="hover" class="block-card">
      <div class="card-head">
        <span class="card-title">详细作业数据</span>
        <el-input
          v-model="tableKeyword"
          clearable
          maxlength="30"
          placeholder="车次"
          style="width: 160px; margin-right: 8px"
          @keyup.enter.native="reloadTable"
        />
        <el-button type="primary" size="small" @click="reloadTable">筛选</el-button>
      </div>
      <el-table v-loading="tableLoading" :data="tableRecords" border stripe style="width: 100%">
        <el-table-column label="卸车线编号" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.noticeId != null ? row.noticeId : row.notice_id }}</template>
        </el-table-column>
        <el-table-column prop="shiftName" label="班次" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="shiftTagType(row.shiftName)" size="small">{{ row.shiftName || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="operationDate" label="作业日期" width="110" />
        <el-table-column prop="unloadTonnage" label="卸车吨位(吨)" width="110" align="right" :formatter="fmtDec" />
        <el-table-column prop="pureOperationHours" label="纯作业时间(h)" width="120" align="right" :formatter="fmtDec" />
        <el-table-column prop="idlingRatePercent" label="设备空转率" width="110" align="right">
          <template slot-scope="{ row }">
            <span :class="idlingClass(row.idlingRatePercent)">{{ fmtNum(row.idlingRatePercent, 1) }}%</span>
          </template>
        </el-table-column>
        <el-table-column prop="throughputPerHour" label="分析(吨/时)" width="110" align="right" :formatter="fmtDec" />
        <el-table-column label="效率评分" min-width="140">
          <template slot-scope="{ row }">
            <div class="eff-cell">
              <span>{{ fmtNum(row.efficiencyScore, 1) }}</span>
              <el-progress
                :percentage="Math.min(100, num(row.efficiencyScore))"
                :show-text="false"
                :stroke-width="8"
                :color="effColor(row.efficiencyScore)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="teamLeaderName" label="班组负责人" width="120" show-overflow-tooltip />
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="pagination.size"
        :current-page.sync="pagination.page"
        @current-change="loadTable"
      />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue';
import { fetchDashboard, fetchDetailPage, fetchTeamOptions } from './api';

export default {
  name: 'RailwayUnloadLineEfficiency',
  components: { NxReportHeader },
  data() {
    const today = new Date();
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return {
      dateRange: [fmt(start), fmt(today)],
      query: {
        lineCodes: [],
        shiftCodes: [],
        teamNo: '',
        efficiencyThreshold: 0
      },
      tableKeyword: '',
      kpi: {},
      lineOptions: [],
      teamOptions: [],
      shiftBar: { categories: [], series: [] },
      idlingTrend: { dateAxis: [], series: [] },
      bubbleList: [],
      tableRecords: [],
      tableTotal: 0,
      tableLoading: false,
      pagination: { page: 1, size: 10 },
      colors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#00bcd4'],
      shiftChart: null,
      idlingChart: null,
      bubbleChart: null,
      sparkChart: null
    };
  },
  computed: {
    /** 接口可能返回 null 元素或 camelCase 字段，避免 v-for 访问 o.line_code 报错 */
    safeLineOptions() {
      const raw = this.lineOptions || [];
      const out = [];
      for (let i = 0; i < raw.length; i++) {
        const item = raw[i];
        if (item == null || typeof item !== 'object') {
          continue;
        }
        const code = item.line_code != null ? item.line_code : item.lineCode;
        if (code == null || code === '') {
          continue;
        }
        const label = item.line_label != null ? item.line_label : item.lineLabel;
        out.push({
          line_code: String(code),
          line_label: label != null && label !== '' ? String(label) : String(code)
        });
      }
      return out;
    },
    safeTeamOptions() {
      const raw = this.teamOptions || [];
      return raw.filter(function (t) {
        return t != null && typeof t === 'object' && t.teamNo != null && t.teamNo !== '';
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.shiftChart = echarts.init(this.$refs.shiftBarRef);
      this.idlingChart = echarts.init(this.$refs.idlingRef);
      this.bubbleChart = echarts.init(this.$refs.bubbleRef);
      if (this.$refs.sparkRef) {
        this.sparkChart = echarts.init(this.$refs.sparkRef);
      }
      window.addEventListener('resize', this.onResize);
      this.loadTeamOptions();
      this.applyFilter();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.shiftChart && this.shiftChart.dispose();
    this.idlingChart && this.idlingChart.dispose();
    this.bubbleChart && this.bubbleChart.dispose();
    this.sparkChart && this.sparkChart.dispose();
  },
  methods: {
    basePayload() {
      const start = this.dateRange && this.dateRange[0];
      const end = this.dateRange && this.dateRange[1];
      return {
        startTime: start ? `${start} 00:00:00` : undefined,
        endTime: end ? `${end} 23:59:59` : undefined,
        lineCodes: this.query.lineCodes && this.query.lineCodes.length ? this.query.lineCodes : undefined,
        shiftCodes: this.query.shiftCodes && this.query.shiftCodes.length ? this.query.shiftCodes : undefined,
        teamNo: this.query.teamNo || undefined,
        efficiencyThreshold:
          this.query.efficiencyThreshold && this.query.efficiencyThreshold > 0 ? this.query.efficiencyThreshold : undefined
      };
    },
    tablePayload() {
      return {
        ...this.basePayload(),
        keyword: this.tableKeyword || undefined,
        page: this.pagination.page,
        size: this.pagination.size
      };
    },
    async loadTeamOptions() {
      try {
        const { data } = await fetchTeamOptions();
        const page = (data && data.data) || {};
        const raw = page.records || [];
        this.teamOptions = raw
          .filter(function (r) {
            return r != null && typeof r === 'object';
          })
          .map((r) => ({
            teamNo: r.teamNo,
            teamName: r.teamName || r.teamNo
          }));
      } catch (e) {
        this.teamOptions = [];
      }
    },
    validateRange() {
      if (!this.dateRange || this.dateRange.length !== 2 || !this.dateRange[0] || !this.dateRange[1]) {
        this.$message.warning('请选择作业日期范围');
        return false;
      }
      const start = new Date(this.dateRange[0]);
      const end = new Date(this.dateRange[1]);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
        this.$message.warning('日期范围不合法');
        return false;
      }
      return true;
    },
    async applyFilter() {
      if (!this.validateRange()) return;
      this.pagination.page = 1;
      await this.loadDashboard();
      await this.loadTable();
    },
    async loadDashboard() {
      try {
        const { data } = await fetchDashboard(this.basePayload());
        const d = data.data || {};
        this.kpi = d.kpi || {};
        this.lineOptions = Array.isArray(d.lineOptions) ? d.lineOptions.filter(function (x) { return x != null; }) : [];
        this.shiftBar = d.shiftBar || { categories: [], series: [] };
        this.idlingTrend = d.idlingTrend || { dateAxis: [], series: [] };
        this.bubbleList = d.bubble || [];
        this.renderShiftBar();
        this.renderIdling();
        this.renderBubble();
        this.renderSpark();
      } catch (e) {
        this.$message.error('看板加载失败');
      }
    },
    renderShiftBar() {
      if (!this.shiftChart) return;
      const cats = this.shiftBar.categories || [];
      const ser = (this.shiftBar.series || [])
        .filter(function (s) {
          return s != null && typeof s === 'object';
        })
        .map((s, i) => ({
          name: s.name,
          type: 'bar',
          barGap: 0,
          emphasis: { focus: 'series' },
          data: (s.data || []).map((v) => (v != null ? Number(v) : null)),
          itemStyle: { color: this.colors[i % this.colors.length] }
        }));
      this.shiftChart.setOption(
        {
          tooltip: { trigger: 'axis' },
          legend: { type: 'scroll', bottom: 0 },
          grid: { left: '3%', right: '4%', bottom: ser.length > 1 ? '18%' : '12%', top: '10%', containLabel: true },
          xAxis: { type: 'category', data: cats },
          yAxis: { type: 'value', name: '分', max: 100 },
          series: ser
        },
        true
      );
    },
    renderIdling() {
      if (!this.idlingChart) return;
      const axis = this.idlingTrend.dateAxis || [];
      const ser = (this.idlingTrend.series || [])
        .filter(function (s) {
          return s != null && typeof s === 'object';
        })
        .map((s, i) => ({
          name: s.name,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          data: (s.data || []).map((v) => (v != null ? Number(v) : null)),
          itemStyle: { color: this.colors[i % this.colors.length] }
        }));
      ser.push({
        name: '目标线',
        type: 'line',
        symbol: 'none',
        lineStyle: { type: 'dashed', color: '#909399' },
        data: axis.map(() => 10)
      });
      this.idlingChart.setOption(
        {
          tooltip: { trigger: 'axis' },
          legend: { type: 'scroll', bottom: 0 },
          grid: { left: '3%', right: '4%', bottom: '18%', top: '10%', containLabel: true },
          xAxis: { type: 'category', data: axis },
          yAxis: { type: 'value', name: '%', min: 0 },
          series: ser
        },
        true
      );
    },
    renderBubble() {
      if (!this.bubbleChart) return;
      const shiftColor = { 早班: '#409EFF', 中班: '#E6A23C', 晚班: '#F56C6C' };
      const byShift = {};
      (this.bubbleList || []).forEach((row) => {
        if (row == null || typeof row !== 'object') {
          return;
        }
        const sh = row.shift_name || row.shiftName || '其他';
        if (!byShift[sh]) byShift[sh] = [];
        const x = Number(row.pure_operation_hours != null ? row.pure_operation_hours : row.pureOperationHours);
        const y = Number(row.unload_tonnage != null ? row.unload_tonnage : row.unloadTonnage);
        if (x > 0 && y >= 0) {
          byShift[sh].push([x, y, Math.sqrt(y + 1) * 2]);
        }
      });
      const series = Object.keys(byShift).map((name) => ({
        name,
        type: 'scatter',
        symbolSize(d) {
          return d[2] || 8;
        },
        data: byShift[name],
        itemStyle: { color: shiftColor[name] || '#909399' }
      }));
      this.bubbleChart.setOption(
        {
          tooltip: {
            trigger: 'item',
            formatter(p) {
              const d = p.data || [];
              return `${p.seriesName}<br/>纯作业时间 ${d[0]}h<br/>卸车吨位 ${d[1]}吨`;
            }
          },
          legend: { top: 0 },
          grid: { left: '8%', right: '6%', top: '14%', bottom: '10%' },
          xAxis: { type: 'value', name: '纯作业时间(h)', scale: true, splitLine: { show: true } },
          yAxis: { type: 'value', name: '卸车吨位(吨)', scale: true, splitLine: { show: true } },
          series
        },
        true
      );
    },
    renderSpark() {
      if (!this.sparkChart) return;
      const raw = this.shiftBar.raw || [];
      const pts = raw
        .filter(function (r) {
          return r != null && typeof r === 'object';
        })
        .map(function (r) {
          return Number(r.avg_score != null ? r.avg_score : r.avgScore);
        })
        .filter(function (v) {
          return !isNaN(v);
        });
      this.sparkChart.setOption(
        {
          grid: { left: 0, right: 0, top: 2, bottom: 0 },
          xAxis: { type: 'category', show: false, data: pts.map((_, i) => i) },
          yAxis: { type: 'value', show: false, min: 0, max: 100 },
          series: [{ type: 'line', data: pts, smooth: true, symbol: 'none', lineStyle: { color: '#67c23a', width: 2 } }]
        },
        true
      );
    },
    async reloadTable() {
      this.pagination.page = 1;
      await this.loadTable();
    },
    async loadTable() {
      this.tableLoading = true;
      try {
        const { data } = await fetchDetailPage(this.tablePayload());
        const page = data.data || {};
        this.tableRecords = page.records || [];
        this.tableTotal = page.total != null ? page.total : 0;
      } catch (e) {
        this.tableRecords = [];
        this.tableTotal = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    exportCsv() {
      const headers = [
        '卸车线编号',
        '班次',
        '作业日期',
        '卸车吨位',
        '纯作业时间h',
        '设备空转率%',
        '吨/时',
        '效率评分',
        '班组负责人'
      ];
      const rows = this.tableRecords.map((r) => [
        r.noticeId != null ? r.noticeId : r.notice_id,
        r.shiftName,
        r.operationDate,
        r.unloadTonnage,
        r.pureOperationHours,
        r.idlingRatePercent,
        r.throughputPerHour,
        r.efficiencyScore,
        r.teamLeaderName
      ]);
      const esc = (c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`;
      const body = [headers.join(','), ...rows.map((line) => line.map(esc).join(','))].join('\r\n');
      const blob = new Blob(['\ufeff' + body], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = '卸车线效能明细.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    },
    onResize() {
      this.shiftChart && this.shiftChart.resize();
      this.idlingChart && this.idlingChart.resize();
      this.bubbleChart && this.bubbleChart.resize();
      this.sparkChart && this.sparkChart.resize();
    },
    num(v) {
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    fmtNum(v, d) {
      if (v == null || v === '') return '—';
      const n = Number(v);
      if (isNaN(n)) return '—';
      return n.toFixed(d);
    },
    fmtDec(r, c, val) {
      return this.fmtNum(val, 2);
    },
    fmtDeltaPct(v) {
      if (v == null || v === '') return '—';
      const n = Number(v);
      if (isNaN(n)) return '—';
      const sign = n > 0 ? '+' : '';
      return `${sign}${n.toFixed(1)}%`;
    },
    deltaClass(v) {
      const n = Number(v);
      if (isNaN(n)) return '';
      return n >= 0 ? 'up' : 'down';
    },
    shiftTagType(sh) {
      if (sh === '早班') return '';
      if (sh === '中班') return 'warning';
      if (sh === '晚班') return 'danger';
      return 'info';
    },
    idlingClass(v) {
      const n = Number(v);
      if (isNaN(n)) return '';
      if (n < 10) return 'ok';
      if (n < 20) return 'mid';
      return 'bad';
    },
    effColor(v) {
      const n = Number(v);
      if (isNaN(n)) return '#909399';
      if (n >= 80) return '#67c23a';
      if (n >= 60) return '#e6a23c';
      return '#f56c6c';
    }
  }
};
</script>

<style scoped lang="scss">
.line-eff-page {
  padding: 10px;
}
.filter-card {
  margin-bottom: 12px;
}
.kpi-row {
  margin-bottom: 16px;
}
.kpi-card {
  min-height: 108px;
  .kpi-val {
    font-size: 26px;
    font-weight: 600;
    color: #303133;
    &.sm {
      font-size: 20px;
    }
    &.warn {
      color: #f56c6c;
    }
    .unit {
      font-size: 14px;
      font-weight: 400;
      color: #909399;
    }
  }
  .kpi-label {
    margin-top: 6px;
    color: #909399;
    font-size: 13px;
  }
  .kpi-sub {
    margin-top: 6px;
    font-size: 12px;
    &.muted {
      color: #909399;
    }
    &.up {
      color: #67c23a;
    }
    &.down {
      color: #f56c6c;
    }
  }
}
.spark {
  height: 36px;
  margin-top: 4px;
}
.block-card {
  margin-bottom: 16px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.card-title {
  font-weight: 600;
  font-size: 15px;
}
.echart-box {
  width: 100%;
  height: 300px;
}
.bubble-box {
  height: 340px;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
.eff-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ok {
  color: #67c23a;
  font-weight: 500;
}
.mid {
  color: #e6a23c;
  font-weight: 500;
}
.bad {
  color: #f56c6c;
  font-weight: 500;
}
</style>
